import { FC, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import Checkbox from "@/components/shared/Checkbox"
import ExportRow, { ExportRange } from "@/components/shared/ExportRow"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import SnackBar from "@/components/shared/SnackBar"
import { useExportRequest } from "@/hooks/useExportRequest"
import { useUserRole } from "@/hooks/useUserRole"
import { AppDispatch, RootState } from "@/store"
import { selectTimezone } from "@/store/facilitiesSlice"
import { exportInmateData, exportInmateLogs } from "@/store/inmatesSlice"
import { Feature } from "@/types/user"

const SNACKBAR_DURATION = 8000
const DATE_FORMAT = "YYYY-MM-DD"

const Export: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const { id } = useParams<{ id: string }>()
   const canAccessExport = useUserRole(Feature.EXPORT)
   const { loadingInmateExport, loadingInmateLogsExport } = useSelector(
      ({ inmatesReducer }: RootState) => inmatesReducer
   )
   const timezone = useSelector(selectTimezone)

   const [alarmsOnly, setAlarmsOnly] = useState(false)
   const { snackbarMessage, closeSnackbar, requestExport } = useExportRequest()

   if (!canAccessExport || !timezone) return null

   const getDays = ({ startDate, endDate }: ExportRange) => ({
      startDate: startDate.tz(timezone).format(DATE_FORMAT),
      endDate: endDate.tz(timezone).format(DATE_FORMAT),
   })

   const handleRequestReport = (range: ExportRange) => {
      if (!id) return
      requestExport(
         () =>
            dispatch(
               exportInmateData({
                  inmate: id,
                  ...getDays(range),
                  timezone,
               })
            ).unwrap(),
         "report"
      )
   }

   const handleRequestLogs = (range: ExportRange) => {
      if (!id) return
      requestExport(
         () =>
            dispatch(
               exportInmateLogs({
                  inmate: id,
                  ...getDays(range),
                  timezone,
                  alarmsOnly,
               })
            ).unwrap(),
         "logs"
      )
   }

   return (
      <Grid className="inmate-detail-overview__export">
         <GridItem size={12}>
            <ExportRow
               title="Request a report"
               description="Select a date range. Your report will be prepared and sent to your email."
               timezone={timezone}
               loading={loadingInmateExport}
               onRequest={handleRequestReport}
            />
         </GridItem>
         <GridItem size={12}>
            <ExportRow
               title="Request logs"
               description="Select a date range. All logs are included unless you select alarms only. Your logs will be prepared and sent to your email."
               timezone={timezone}
               loading={loadingInmateLogsExport}
               onRequest={handleRequestLogs}
               extraControls={
                  <Checkbox
                     id="export-alarms-only"
                     label="Only alarms"
                     checked={alarmsOnly}
                     onChange={setAlarmsOnly}
                  />
               }
            />
         </GridItem>
         <SnackBar
            isOpen={!!snackbarMessage}
            message={snackbarMessage}
            duration={SNACKBAR_DURATION}
            onClose={closeSnackbar}
         />
      </Grid>
   )
}

export default Export
